import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  CheckCircle2,
  XCircle,
  Clock3,
  Home,
  CreditCard,
  ShieldCheck,
} from "lucide-react";
import "./PaymentSuccess.css";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
);

type PaymentResultState = {
  bookingId?: string;
  amount?: number;
  currency?: string;
  paymentStatus?: string;
  message?: string;
};

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const location = useLocation();

  const routeState = location.state as PaymentResultState | null;

  const [paymentStatus, setPaymentStatus] = useState(
    routeState?.paymentStatus || "loading"
  );

  const [message, setMessage] = useState(routeState?.message || "");

  useEffect(() => {
    const checkPaymentStatus = async () => {
      const params = new URLSearchParams(window.location.search);
      const clientSecret = params.get("payment_intent_client_secret");

      if (!clientSecret) {
        if (routeState?.paymentStatus) return;

        setPaymentStatus("failed");
        setMessage("Não foi possível confirmar o estado do pagamento.");
        return;
      }

      const stripe = await stripePromise;

      if (!stripe) {
        setPaymentStatus("failed");
        setMessage("Stripe não foi carregado corretamente.");
        return;
      }

      const { paymentIntent, error } = await stripe.retrievePaymentIntent(
        clientSecret
      );

      if (error) {
        setPaymentStatus("failed");
        setMessage(error.message || "Erro ao confirmar o pagamento.");
        return;
      }

      if (!paymentIntent) {
        setPaymentStatus("failed");
        setMessage("Pagamento não encontrado.");
        return;
      }

      setPaymentStatus(paymentIntent.status);
    };

    checkPaymentStatus();
  }, [routeState?.paymentStatus]);

  useEffect(() => {
    const isConversion =
      paymentStatus === "succeeded" || paymentStatus === "pay_on_arrival";

    if (!isConversion) return;

    const conversionKey = routeState?.bookingId
      ? `google_ads_conversion_${routeState.bookingId}`
      : "google_ads_conversion";

    if (sessionStorage.getItem(conversionKey)) return;

    if (window.gtag) {
      window.gtag("event", "conversion", {
        send_to: "AW-18172511820/_o7NCO37trAcEMyMqtlD",
      });

      sessionStorage.setItem(conversionKey, "true");
    }
  }, [paymentStatus, routeState?.bookingId]);

  const formattedAmount = useMemo(() => {
    if (typeof routeState?.amount !== "number") return null;

    return new Intl.NumberFormat("pt-PT", {
      style: "currency",
      currency: (routeState.currency || "EUR").toUpperCase(),
    }).format(routeState.amount);
  }, [routeState?.amount, routeState?.currency]);

  const isPaid = paymentStatus === "succeeded";
  const isProcessing = paymentStatus === "processing";
  const isRequiresAction = paymentStatus === "requires_action";
  const isPayOnArrival = paymentStatus === "pay_on_arrival";
  const isLoading = paymentStatus === "loading";

  const isPendingPayment = isProcessing || isRequiresAction;

  const isSuccess = isPaid || isPendingPayment || isPayOnArrival;

  const title = isLoading
    ? "A confirmar pagamento"
    : isPayOnArrival
    ? "Reserva confirmada"
    : isPaid
    ? "Pagamento concluído"
    : isPendingPayment
    ? "Referência de pagamento criada"
    : "Pagamento não concluído";

  const description = isLoading
    ? "Estamos a verificar o estado do pagamento."
    : isPayOnArrival
    ? "A tua reserva foi criada com sucesso. O pagamento será feito diretamente ao motorista no dia do serviço."
    : isPaid
    ? "Recebemos o teu pagamento e a tua reserva foi confirmada com sucesso."
    : isPendingPayment
    ? "Receberás um email com os dados de pagamento. A tua reserva será confirmada automaticamente assim que o pagamento for concluído."
    : message ||
      "Não foi possível concluir o pagamento. Podes tentar novamente ou escolher outro método.";

  const statusLabel = isLoading
    ? "A confirmar"
    : isPayOnArrival
    ? "Pagamento no dia"
    : isPaid
    ? "Pago"
    : isPendingPayment
    ? "A aguardar pagamento"
    : "Falhou";

  return (
    <div className="payment-result-page">
      <div className="payment-result-card">
        <div
          className={`payment-result-icon ${
            isSuccess || isLoading ? "success" : "error"
          }`}
        >
          {isSuccess || isLoading ? (
            <CheckCircle2 size={34} />
          ) : (
            <XCircle size={34} />
          )}
        </div>

        <div className="payment-result-header">
          <span className="payment-result-badge">
            {isLoading
              ? "A verificar"
              : isPaid
              ? "Pagamento confirmado"
              : isPendingPayment
              ? "Pagamento pendente"
              : isPayOnArrival
              ? "Pedido recebido"
              : "Ação necessária"}
          </span>

          <h1>{title}</h1>
          <p>{description}</p>
        </div>

        <div className="payment-result-details">
          <div className="payment-result-row">
            <div>
              <CreditCard size={17} />
              <span>Estado</span>
            </div>

            <strong>{statusLabel}</strong>
          </div>

          {routeState?.bookingId && (
            <div className="payment-result-row">
              <div>
                <ShieldCheck size={17} />
                <span>Reserva</span>
              </div>

              <strong>{routeState.bookingId}</strong>
            </div>
          )}

          {formattedAmount && (
            <div className="payment-result-row">
              <div>
                <CreditCard size={17} />
                <span>Total</span>
              </div>

              <strong>{formattedAmount}</strong>
            </div>
          )}

          {isPendingPayment && (
            <div className="payment-result-warning">
              <Clock3 size={16} />
              <span>
                Se escolheste Multibanco, usa a entidade e referência enviadas
                por email para concluir o pagamento. Após o pagamento, receberás
                a confirmação final da reserva.
              </span>
            </div>
          )}
        </div>

        <div className="payment-result-actions">
          {!isSuccess && !isLoading && (
            <button
              type="button"
              className="payment-result-secondary"
              onClick={() => navigate(-1)}
            >
              Tentar novamente
            </button>
          )}

          <button
            type="button"
            className="payment-result-primary"
            onClick={() => navigate("/")}
          >
            <Home size={17} />
            Voltar ao início
          </button>
        </div>
      </div>
    </div>
  );
}